import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableCell,
} from "@mui/material";
import VantageListHead from "../../../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  formatValue,
  getComparator,
} from "../../../../../component/Methods";
import Legend from "../../../Components/Legend";
import {
  TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA_DELTA,
  TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA,
} from "../../../../../component/Methods";

const isEmpty = (value) => value === undefined || value === null || value === 0;

const formatNumericValue = (value, decimals = 4) => {
  if (isEmpty(value)) return "-";
  return value < 0
    ? `(${Math.abs(value).toFixed(decimals)})`
    : value.toFixed(decimals);
};

const formatShareCount = (value) => {
  if (isEmpty(value)) return "-";
  const absValue = Math.abs(value);
  return value < 0
    ? `(${formatValue(absValue, true)})`
    : `${formatValue(value, true)}\u00A0`;
};

const getValueColor = (value, theme) => {
  if (isEmpty(value)) return theme.palette.text.primary;
  return value > 0 ? theme.palette.success.main : theme.palette.error.main;
};

const getSignalColor = (signal, theme) => {
  if (isEmpty(signal)) return theme.palette.text.primary;
  if (signal > 0) return theme.palette.success.main;
  if (signal === 0) return theme.palette.text.primary;
  return theme.palette.error.main;
};

const commonCellStyles = (theme) => ({
  fontSize: "12px",
  paddingLeft: "5px",
  borderLeft: "none",
  borderRight: "none",
  borderColor: theme.palette.divider,
});

const ExistingFundRebalance = ({
  existingNewArray,
  hideDeltaData,
  deltaRebalancer,
  rebalanceType,
  tableRef,
  onScroll,
  fillHeight = false,
}) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("ticker");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const filteredUsers = applySortFilter(
      existingNewArray,
      getComparator(order, orderBy)
    );
    setData(filteredUsers);
  }, [order, orderBy, existingNewArray]);

  const closingPositions = existingNewArray?.filter(
    (item) => item.signal === 0
  ).length;
  const newPositions = existingNewArray?.filter(
    (item) => item.CurrentWeight === 0 && item.signal !== 0
  ).length;

  const deltaImpactedCount = existingNewArray?.filter(
    (item) => item.deltaWeight !== item.RequiredWeight
  ).length;

  const showDeltaColumns =
    rebalanceType === "specific" && !hideDeltaData && deltaRebalancer !== 0;

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "4px",
          marginTop: "1px",
          width: "100%",
          marginRight: 1,
          backgroundColor: theme.palette.background.paper,
          ...(fillHeight && {
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }),
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "18px",
            mb: 1,
            color: theme.palette.text.primary,
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: "auto", maxWidth: "800px", pt: 2, pb: 0, pl: 2 }}>
            <Legend closingCount={closingPositions} newCount={newPositions} deltaImpactedCount={deltaImpactedCount} />
          </Box>
        </Typography>

        <TableContainer
          sx={{
            width: "100%",
            boxSizing: "border-box",
            ...(fillHeight
              ? {
                  flex: "1 1 0px",
                  minHeight: 0,
                  height: 0,
                  maxHeight: "100%",
                  overflow: "auto",
                  display: "block",
                }
              : {
                  height: "450px",
                  overflow: "auto",
                }),
          }}
        >
          <Table
            size="small"
            stickyHeader
            aria-label="a dense table"
            sx={{
              height: "max-content",
              borderCollapse: "collapse",
            }}
          >
         
             
                <TableHead>
                  <TableRow
                    sx={{
                      "&:hover": {
                        background: theme.palette.grey[200],
                        cursor: "pointer",
                      },
                    }}
                  >
                    <TableCell
                      align="left"
                      colSpan={2}
                      sx={{
                        fontSize: theme.typography.bodyText12.fontSize,
                        fontWeight: 900,
                        backgroundColor: "#34495E",
                        padding: 0,
                        paddingLeft: "20px",
                        textAlign: "left",
                      }}
                    ></TableCell>

                    <TableCell
                      align="center"
                      colSpan={2}
                      sx={{
                        fontSize: theme.typography.bodyText12.fontSize,
                        fontWeight: 900,
                        backgroundColor: "#34495E",
                        padding: 0,
                        paddingLeft: "20px",
                        textAlign: "center",
                        color: "#FFFFFF",
                        borderRight: "2px solid #ffffff",
                        borderLeft: "2px solid #ffffff",
                      }}
                    >
                      Existing Positions
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={2}
                      sx={{
                        fontSize: theme.typography.bodyText12.fontSize,
                        fontWeight: 900,
                        backgroundColor: "#34495E",
                        padding: 0,
                        paddingLeft: "20px",
                        textAlign: "center",
                        color: "#FFFFFF",
                        borderRight: "2px solid #ffffff",
                      }}
                    >
                      Difference
                    </TableCell>
{showDeltaColumns ?                     
                    <TableCell
                      align="center"
                      colSpan={2}
                      sx={{
                        fontSize: theme.typography.bodyText12.fontSize,
                        fontWeight: 900,
                        backgroundColor: "#34495E",
                        padding: 0,
                        paddingLeft: "20px",
                        textAlign: "center",
                        color: "#FFFFFF",
                      }}
                    >
                      Drift Rebalancer{" "}
                    </TableCell>
                    : null}
                  </TableRow>
                </TableHead>
             
           
            <VantageListHead
              order={order}
              orderBy={orderBy}
              headLabel={
                showDeltaColumns
                  ? TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA_DELTA
                  : TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA
              }
              noStatus={true}
              customizeFont={12}
              rowCount={data.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {data?.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor:
                      row.deltaWeight !== row.RequiredWeight
                        ? "rgba(25, 118, 210, 0.15)"
                        : row.CurrentWeight === 0 && row.signal != 0
                        ? "rgba(76, 175, 80, 0.1)"
                        : row.signal === 0
                        ? "rgba(244, 67, 54, 0.1)"
                        : theme.palette.background.paper,
                    "&:hover": {
                      background: theme.palette.action.hover,
                      cursor: "pointer",
                    },
                  }}
                >
                  <Tablecell
                    align="left"
                    sx={{
                      ...commonCellStyles(theme),
                      color: theme.palette.text.primary,
                    }}
                  >
                    {row?.ticker?.toUpperCase() || ""}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    sx={{
                      ...commonCellStyles(theme),
                      color: getSignalColor(row?.signal, theme),
                    }}
                  >
                    {formatNumericValue(row?.signal)}
                  </Tablecell>

                  <Tablecell
                    align="right"
                    sx={{
                      ...commonCellStyles(theme),
                      color: getValueColor(row?.CurrentWeight, theme),
                    }}
                  >
                    {formatNumericValue(row?.CurrentWeight)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    sx={{
                      ...commonCellStyles(theme),
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatShareCount(row?.shareCountCurrent)}
                  </Tablecell>
                  {showDeltaColumns ? (
                    <>
                      <Tablecell
                        align="right"
                        sx={{
                          ...commonCellStyles(theme),
                          color: getValueColor(row?.deltaWeight, theme),
                        }}
                      >
                        {formatNumericValue(row?.deltaWeight)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        sx={{
                          ...commonCellStyles(theme),
                          color: theme.palette.text.primary,
                        }}
                      >
                        {formatShareCount(row?.shareCountDelta)}
                      </Tablecell>
                    </>
                  ) : null}
                  <Tablecell
                    align="right"
                    sx={{
                      ...commonCellStyles(theme),
                      color: getValueColor(row?.RequiredWeight, theme),
                    }}
                  >
                    {isEmpty(row?.RequiredWeight) ? (
                      "-"
                    ) : (
                      <Typography
                        sx={{
                          color: getValueColor(row?.RequiredWeight, theme),
                        }}
                      >
                        {formatNumericValue(row?.RequiredWeight)}
                      </Typography>
                    )}
                  </Tablecell>

                  <Tablecell
                    align="right"
                    sx={{
                      ...commonCellStyles(theme),
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatShareCount(row?.shareCountRequired)}
                  </Tablecell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default ExistingFundRebalance;
